import type React from 'react'
import { useCallback, useRef } from 'react'
import { useActiveObject } from '@/components/app/editor/active-object-context'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import type { BatchShapeTransformItem } from '@/lib/transforms/types/batch-shape-transform-item'
import type { ShapeManagerRef } from '@/lib/shapes/types/shape-manager-ref'
import { expandTransparencyShapeTargets } from '@/lib/object-effects/expand-transparency-targets'
import {
  clampTransparencyPercent,
  opacityFromTransparency,
  transparencyFromOpacity,
} from '@/lib/object-effects/transparency'
import { dispatchShapeCommit } from '../helpers/dispatch-shape-commit'
import type { EffectControlProps } from '../types/effect-control-props'

interface TransparencyDocumentOptions {
  activeObjects: ShapeData[]
  shapeManagerRef: React.RefObject<ShapeManagerRef | null> | undefined
  onShapeCommitted: EffectControlProps['onShapeCommitted']
  onBatchShapesCommitted: EffectControlProps['onBatchShapesCommitted']
}

/**
 * Document side of Transparency: session start values, the live opacity apply (group shells
 * included, locked targets skipped) and the history commit (group shells excluded).
 */
export function useTransparencyDocument({
  activeObjects,
  shapeManagerRef,
  onShapeCommitted,
  onBatchShapesCommitted,
}: TransparencyDocumentOptions) {
  const { setActiveObject, setActiveObjects } = useActiveObject()
  const sessionStartsRef = useRef<Map<string, number>>(new Map())

  const refreshActiveFromManagers = useCallback(() => {
    const sm = shapeManagerRef?.current

    if (sm) {
      const all = sm.getShapes()

      if (activeObjects.length === 1) {
        const one = all.find((s) => s.id === activeObjects[0].id)

        if (one) setActiveObject(one)
      }

      if (activeObjects.length > 1) {
        setActiveObjects(activeObjects.map((o) => all.find((s) => s.id === o.id) ?? o))
      }
    }
  }, [activeObjects, setActiveObject, setActiveObjects, shapeManagerRef])

  const applyTransparencyLive = useCallback(
    (value: number) => {
      const sm = shapeManagerRef?.current
      const v = clampTransparencyPercent(value)
      const opacity = opacityFromTransparency(v)
      const allShapes = sm?.getShapes() ?? []
      const shapeTargets = expandTransparencyShapeTargets(activeObjects, allShapes, {
        includeGroupShell: true,
      })

      for (const s of shapeTargets) {
        if (s.locked) continue
        sm?.updateShape(s.id, { opacity })
      }

      refreshActiveFromManagers()
    },
    [activeObjects, refreshActiveFromManagers, shapeManagerRef]
  )

  /** @returns true when a history entry was committed */
  const commitWithHistory = useCallback(
    (nextValue: number): boolean => {
      const sm = shapeManagerRef?.current
      const v = clampTransparencyPercent(nextValue)
      const shapeItems: BatchShapeTransformItem[] = []
      const allShapes = sm?.getShapes() ?? []
      const shapeTargets = expandTransparencyShapeTargets(activeObjects, allShapes)

      for (const s of shapeTargets) {
        if (s.locked) continue
        const key = `shape:${s.id}`
        const start = sessionStartsRef.current.get(key) ?? transparencyFromOpacity(s.opacity)

        if (start === v) continue
        shapeItems.push({
          shapeId: s.id,
          before: { opacity: opacityFromTransparency(start) },
          after: { opacity: opacityFromTransparency(v) },
        })
      }

      return dispatchShapeCommit(shapeItems, sm, onShapeCommitted, onBatchShapesCommitted)
    },
    [activeObjects, onBatchShapesCommitted, onShapeCommitted, shapeManagerRef]
  )

  const beginSession = useCallback(() => {
    const sm = shapeManagerRef?.current
    const allShapes = sm?.getShapes() ?? []
    const shapeTargets = expandTransparencyShapeTargets(activeObjects, allShapes)

    sessionStartsRef.current = new Map()

    for (const s of shapeTargets) {
      sessionStartsRef.current.set(`shape:${s.id}`, transparencyFromOpacity(s.opacity))
    }
  }, [activeObjects, shapeManagerRef])

  const clearSession = useCallback(() => {
    sessionStartsRef.current.clear()
  }, [])

  return { applyTransparencyLive, commitWithHistory, beginSession, clearSession }
}
